import React, { useRef, useState } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { TextField, Box, Typography, Paper } from '@mui/material';
import BaseModal from '../../../utils/BaseModal';
import api from '../../../../api/axios';
import { toast } from 'react-toastify';

type Props = {
  open: boolean;
  selectedTemplate: any;
  onClose: () => void;
  onSuccess: () => void;
};

const CreateNewsletterModal: React.FC<Props> = ({ open, selectedTemplate, onClose, onSuccess }) => {
  const emailEditorRef = useRef<EditorRef>(null);
  const [subject, setSubject] = useState('');
  const [scheduledAt, setscheduledAt] = useState('');
  const [loading, setLoading] = useState(false);

  // Load design when editor is ready
  const onEditorReady = () => {
    const unlayer = emailEditorRef.current?.editor;
    if (unlayer && selectedTemplate?.designJson) {
      unlayer.loadDesign(JSON.parse(selectedTemplate.designJson));
    }
  };

  const handleSend = () => {
    if (!subject) {
      toast.error("Subject required");
      return;
    }

    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) {
      toast.error("Editor not ready");
      return;
    }

    unlayer.exportHtml(async (data) => {
      const { html } = data;
      setLoading(true);
      try {
        await api.post('/system/newsletter/send', {
          subject,
          scheduledAt: scheduledAt || new Date().toISOString(),
          contentHtml: html,
          templateId: selectedTemplate.id,
          status: scheduledAt ? 'SCHEDULED' : 'PENDING'
        });

        toast.success("Campaign initiated!");
        onSuccess();
        onClose();
      } catch (e) {
        console.error("Campaign Error:", e);
        toast.error("Failed to send campaign");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
      <BaseModal
          open={open}
          title="Launch Campaign"
          onAbort={onClose}
          onSubmit={handleSend}
          submitText={loading ? "Processing..." : "Start Campaign"}
          maxWidth="sm"
      >
        <Box className="flex flex-col gap-6">
          <Paper className="p-3 bg-blue-500/10 border border-blue-500/20">
            <Typography className="text-[10px] uppercase font-black text-blue-400">
              Selected Template
            </Typography>
            <Typography className="text-white font-bold">
              {selectedTemplate?.name || 'No Template Selected'}
            </Typography>
          </Paper>

          <TextField
              fullWidth
              label="Email Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              variant="filled"
              sx={{
                '& .MuiInputBase-root': { borderRadius: 0, backgroundColor: 'rgba(0,0,0,0.2)' },
                input: { color: 'white' }
              }}
          />

          <TextField
              fullWidth
              label="Schedule (Leave empty for instant send)"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setscheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="filled"
              sx={{
                '& .MuiInputBase-root': { borderRadius: 0, backgroundColor: 'rgba(0,0,0,0.2)' },
                input: { color: 'white' }
              }}
          />

          {/* Hidden Editor for HTML generation */}
          <div style={{ display: 'none' }}>
            <EmailEditor
                ref={emailEditorRef}
                onReady={onEditorReady}
            />
          </div>
        </Box>
      </BaseModal>
  );
};

export default CreateNewsletterModal;